import { SearchOutlined } from '@ant-design/icons';
import { Button, Col, DatePicker, Divider, Form, Input, message, Row, Select } from 'antd';
import { RangePickerProps } from 'antd/es/date-picker';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { IListColumn, ISortColumn, PaginationResponse } from 'src/@types/search-params';
import { getTopupHistory, getTopupName } from 'src/api/register_and_cancel_and_load_package';
import TableContent from 'src/components/CTable';
import useWindowSize from 'src/hooks/useWindowSize';
import { useAppSelector } from 'src/redux/hook';
import { setGlobalState } from 'src/redux/slice/globalSlice';
import { SimStatusEnum } from 'src/shared/enum/change_sim';
import { IConnectionBatch, IOverFlowOrderDetailResponse } from 'src/shared/interface/connection';
import { IOrderStatus, IPackageAssigned } from 'src/shared/interface/package_assigned';
import {
  exportExcel,
  formatDate,
  formatMoney,
  formatPhoneNumberV2,
  parseQueryString,
} from 'src/utils';
import { requestTypeToString } from 'src/utils/register_cancel';
import CGridView from 'src/components/CGridView';
import { usePermission } from 'src/hooks/usePermission';

const HistoryTopup = () => {
  const size = useWindowSize();
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [_, setSearchParams] = useSearchParams();
  const [form] = Form.useForm();
  const merchantInfo: string | null = localStorage.getItem('merchantInfo');
  const { openTabs, activeKeyId } = useAppSelector((state) => state.global);
  const [sortColumn] = useState<ISortColumn>();

  const [data, setData] = useState<PaginationResponse<IConnectionBatch[]>>();
  const [dataTopupName, setDataTopupName] = useState<IOrderStatus[]>([]);

  const { hasPermission, PERMISSIONS } = usePermission();

  const disabledDate: RangePickerProps['disabledDate'] = (current) => {
    return current && current >= moment().endOf('day');
  };

  const handleFormSearch = (value: any) => {
    let values = { ...value };
    values = {
      ...values,
      search: values.search?.trim(),
      dateFrom:
        value.dateRange && value?.dateRange[0]
          ? value?.dateRange[0].format('YYYY-MM-DD')
          : value.dateFrom ?? undefined,
      dateTo:
        value.dateRange && value?.dateRange[1]
          ? value?.dateRange[1].format('YYYY-MM-DD')
          : value.dateTo ?? undefined,
      dateRange: undefined,
      pageIndex: 1,
      pageSize: 10,
    };
    Object.keys(values).forEach((key) => {
      if (!values[key]) {
        delete values[key];
      }
    });
    fetchListConnectionHistory(values);
    setSearchParams(values);
  };

  const fetchListConnectionHistory = async (params = {}) => {
    const res = await getTopupHistory(params);
    if (res) {
      setData(res);
    }
  };

  const fetchGetTopupName = async () => {
    await getTopupName().then((res: any) => {
      if (res) {
        const dataPackage: IOrderStatus[] = [{ label: 'Tất cả', value: '' }];
        res?.map((item: any) => {
          dataPackage?.push({
            label: item.name,
            value: item.name,
          });
        });
        setDataTopupName(dataPackage);
      }
    });
  };
  useEffect(() => {
    fetchGetTopupName();
    fetchListConnectionHistory();
    form.resetFields();
  }, [activeKeyId]);

  const handleDetailTopup = (order: any) => {
    dispatch(
      setGlobalState({
        activeKeyMenu: 'topup/detail' + '?id=' + order.orderId,
      }),
    );
    if (!openTabs.find((tab) => tab.key === 'topup/detail' + '?id=' + order.orderId)) {
      dispatch(
        setGlobalState({
          openTabs: [
            ...openTabs,
            {
              label: 'Xem chi tiết Nạp tiền ' + order.orderId,
              children: '',
              key: 'topup/detail' + '?id=' + order.orderId,
              closable: true,
            },
          ],
        }),
      );
    }
    navigate(`/management/topup/detail?id=${order.orderId}`);
  };

  const handleExport = () => {
    const link = document.createElement('a');
    link.target = '_blank';
    link.download = `lich_su_nap_tien_${moment().format('DDMMYYYYHHmmss')}.xlsx`;

    const url =
      process.env.REACT_APP_API_URL + '/api/topup-provider/export-history-topup' + location.search;
    exportExcel(url)
      .then((res) => {
        link.href = URL.createObjectURL(new Blob([res.data], { type: 'application/vnd.ms-excel' }));
        link.click();
      })
      .catch((error) => {
        if (error.status === 403) {
          message.error(`Tài khoản của bạn không có quyền truy cập vào chức năng này`);
        }
      });
  };

  const columns: IListColumn[] = [
    { name: 'STT', fieldName: 'index', width: 20, isHideSort: true },
    {
      name: 'Mã đơn',
      fieldName: 'orderId',
      renderSpecialCard: (record) => {
        return (
          <button
            disabled={!hasPermission(PERMISSIONS.TOPUP.VIEW_DETAIL)}
            className={`underline md:whitespace-nowrap text-right cursor-pointer bg-transparent border-transparent ${
              hasPermission(PERMISSIONS.TOPUP.VIEW_DETAIL) ? 'text-blue-400' : 'text-gray-400'
            } font-semibold`}
            onClick={() => handleDetailTopup(record)}
          >
            {record.orderId ?? ''}
          </button>
        );
      },
      isHideSort: true,
    },
    {
      name: 'Loại',
      fieldName: 'type',
      renderSpecialCard: (record) => {
        return <span>{record.type && requestTypeToString(record.type)}</span>;
      },
      isHideSort: true,
    },
    {
      name: 'STB',
      fieldName: 'phoneNumber',
      isHideSort: true,
      renderSpecialCard: (record) => {
        return <span>{formatPhoneNumberV2(record?.phoneNumber)}</span>;
      },
    },
    {
      name: 'Mệnh giá',
      fieldName: 'topupName',
      isHideSort: true,
      renderSpecialCard: (record: IPackageAssigned) => {
        return <span>{record.price ? record.topupName + 'đ' : 0}</span>;
      },
    },
    {
      name: 'Thành tiền',
      fieldName: 'price',
      isHideSort: true,
      renderSpecialCard: (record: IPackageAssigned) => {
        return <span>{record.price ? formatMoney(record.price, 'đ') : 0}</span>;
      },
    },
    { name: 'Trạng thái', fieldName: 'statusString', isHideSort: true },
    { name: 'Mã lỗi', fieldName: 'errorCode', isHideSort: true },
    { name: 'Mô tả lỗi', fieldName: 'errorMessage', isHideSort: true },
    { name: 'Người tạo', fieldName: 'createdBy', isHideSort: false },
    {
      name: 'Ngày tạo',
      fieldName: 'createdAt',
      renderSpecialCard: (record) => {
        return <span>{record.createdAt ? formatDate(record.createdAt) : ''}</span>;
      },
    },
    { name: 'Người cập nhật cuối', fieldName: 'updatedBy' },
    {
      name: 'Ngày cập nhật cuối',
      fieldName: 'updatedAt',
      renderSpecialCard: (record) => {
        return <span>{record.updatedAt ? formatDate(record.updatedAt) : ''}</span>;
      },
    },
  ];

  return (
    <div>
      <Form
        onFinish={handleFormSearch}
        layout={size.width < 768 ? 'vertical' : 'inline'}
        className='gap-2'
        initialValues={{
          topupName: '',
          StatusString: '',
        }}
        form={form}
      >
        <Form.Item name='search'>
          <Input
            prefix={<SearchOutlined />}
            placeholder='Mã đơn, Số thuê bao'
            className='w-[400px]'
            allowClear
          />
        </Form.Item>
        <Form.Item name='topupName' label='Mệnh giá'>
          <Select
            showSearch
            className='min-w-[150px]'
            placeholder='Vui lòng chọn'
            options={dataTopupName}
          ></Select>
        </Form.Item>
        <Form.Item name='StatusString' label='Trạng thái'>
          <Select
            showSearch
            className='min-w-[150px]'
            placeholder='Vui lòng chọn'
            options={[
              {
                value: '',
                label: 'Tất cả',
              },
              {
                value: SimStatusEnum.Success,
                label: 'Success',
              },
              {
                value: SimStatusEnum.Failed,
                label: 'Failed',
              },
              {
                value: SimStatusEnum.New,
                label: 'New',
              },
              {
                value: SimStatusEnum.Paid,
                label: 'Paid',
              },
              {
                value: SimStatusEnum.Processing,
                label: 'Processing',
              },
              {
                value: SimStatusEnum.Canceled,
                label: 'Canceled',
              },
              {
                value: SimStatusEnum.Pending,
                label: 'Pending',
              },
            ]}
          >
            {/* {renderOptions(listStatus)} */}
          </Select>
        </Form.Item>
        <Form.Item name='' label='Người tạo'>
          <Select
            showSearch
            className='min-w-[150px]'
            defaultValue={merchantInfo ? JSON.parse(merchantInfo).merchantName : ''}
          >
            {/* <Select.Option value='merchantName'>Tất cả</Select.Option> */}
            <Select.Option value='merchantName'>
              {merchantInfo ? JSON.parse(merchantInfo).merchantName : ''}
            </Select.Option>
          </Select>
        </Form.Item>
        <Form.Item name='dateRange' label='Ngày tạo'>
          <DatePicker.RangePicker
            disabledDate={disabledDate}
            placeholder={['Từ ngày', 'đến ngày']}
            format={['DD/MM/YYYY', 'DD/MM/YYYY']}
          />
        </Form.Item>
        <div className='row-button' style={{ justifyContent: 'flex-end', margin: '1.5rem 0' }}>
          <Button type='primary' htmlType='submit'>
            Tìm kiếm
          </Button>

          {hasPermission(PERMISSIONS.TOPUP.HISTORY_EXPORT) && (
            <Button onClick={handleExport}>Export</Button>
          )}
        </div>
      </Form>
      <div className='mt-6'>
        {size.width < 1024 ? (
          <CGridView
            length={data?.results.length}
            totalRecord={data?.rowCount ?? 0}
            columns={columns}
            title={() => <div className='text-xl font-bold my-3'>Danh sách lịch sử nạp tiền</div>}
          >
            {data?.results && data?.results.length > 0 ? (
              data?.results.map((item: IOverFlowOrderDetailResponse) => {
                return (
                  <div key={'data-row-' + item.id}>
                    <Divider className='border-t-black' />

                    <Row className='mb-6 items-center' gutter={[4, 16]}>
                      {columns.map((ele) => {
                        if (ele.fieldName !== 'index' && ele.fieldName !== 'action') {
                          return (
                            <>
                              <Col span={11} className={`font-bold label`}>
                                <div className='flex gap-2 items-center'>{ele.name}</div>
                              </Col>
                              <Col className='break-words text-right' span={13}>
                                {ele?.renderSpecialCard
                                  ? ele?.renderSpecialCard(item, null, null, null)
                                  : item[`${ele?.fieldName}`]}
                              </Col>
                            </>
                          );
                        } else if (ele.fieldName === 'action') {
                          return (
                            <Col key={'column-' + item.id + ele.fieldName} span={24}>
                              <div className='flex gap-6'>
                                {ele?.renderSpecialCard
                                  ? ele?.renderSpecialCard(item, null, null, null)
                                  : item[`${ele?.fieldName}`]}
                              </div>
                            </Col>
                          );
                        } else {
                          return <></>;
                        }
                      })}
                    </Row>
                  </div>
                );
              })
            ) : (
              <p>{'Không có dữ liệu'}</p>
            )}
          </CGridView>
        ) : (
          <TableContent
            bordered
            scroll={{ x: 'max-content' }}
            locale={{ emptyText: 'Không tìm thấy kết quả' }}
            dataSource={data?.results ?? []}
            columns={columns}
            totalRecord={data?.rowCount ?? 0}
            sortColumn={sortColumn}
            onPageChange={(pageNumber, pageSize) => {
              const pagination = parseQueryString(location.search);
              fetchListConnectionHistory({
                ...pagination,
                pageIndex: pageNumber ?? 1,
                pageSize: pageSize ?? 10,
                sortField: pagination.sortField ?? undefined,
                asc: pagination.asc ?? undefined,
              });
            }}
            onSort={(sort: ISortColumn) => {
              const pagination = parseQueryString(location.search);
              fetchListConnectionHistory({
                ...pagination,
                pageIndex: pagination.pageIndex ?? 1,
                pageSize: pagination.pageSize ?? 10,
                sortField: sort.fieldName,
                asc:
                  sort.orderType === 'asc' ? true : sort.orderType === 'desc' ? 'false' : undefined,
              });
            }}
          />
        )}
      </div>
    </div>
  );
};

export default HistoryTopup;
